import {Component, forwardRef, Inject} from "@angular/core";
import {DialogComponent} from "../bootstrap-modal/dialog.component";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {AssetService} from "./asset.service";
import {ContactService} from "../contact/contact.service";
import {ProjectService} from "../project/project.service";
import {WorkflowService} from "../workflow/workflow.service";
import {handleErrors} from "../misc/handle-errors.decorator";
import {FormGroup} from "@angular/forms";
import {ItsmContactModel} from "../contact/itsm-contact.model";
import {exposeComponentInDebugMode} from "../misc/expose-component-in-debug-mode.decorator";
import {AccessoryService} from "../accessory/accessory.service";
import {toggleLoading} from "../misc/loading.decorator";
import {get} from "lodash";
import {AssetBundledAccessoryModel, AssetDispositionModel, AssetGqlModel} from "./asset.gql-model";
import {StandaloneAccessoryGqlModel} from "../accessory/standalone-accessory.gql-model";
import {IDatePickerOptions} from "../datepicker/datepicker.component";
import {ItsmAddressModel} from "../address/itsm-address.model";
import {AddressService} from "../address/address.service";
import {AppSettingsService} from "../app-settings/app-settings.service";

import * as moment from "moment";

export interface EditAssetDialogModel {
  projectId: string;
  assetId: string;
  addresses: ItsmAddressModel[];
  contacts: ItsmContactModel[];
}
export interface EditAssetDialogReturnModel {
  addresses: ItsmAddressModel[];
  contacts: ItsmContactModel[];
  updated: boolean;
}

@Component({
  templateUrl: "./edit-asset-dialog.template.pug",
})
@exposeComponentInDebugMode()
export class EditAssetDialogComponent extends DialogComponent<EditAssetDialogModel, EditAssetDialogReturnModel>
  implements EditAssetDialogModel {
  projectId: string;
  assetId: string;
  addresses: ItsmAddressModel[];
  contacts: ItsmContactModel[];

  enableAdditionalFieldsForMigration: boolean = false;

  loading: boolean = true;
  targetSystem: string;

  result: EditAssetDialogReturnModel = {
    addresses: [],
    contacts: [],
    updated: false,
  };

  page = "details";
  error: Error;

  installDateOpts: IDatePickerOptions = {};
  asset: AssetGqlModel = new AssetGqlModel({});
  form: FormGroup = this.asset.formGroup;

  // Address and Contact Parameters
  // Contacts
  contactMap: any = {};
  settableContacts: ItsmContactModel[];
  settableContactsMap: any = {};

  // Address / Buildings
  addressMap: any = {};
  settableAddresses: ItsmAddressModel[] = [];
  settableAddressesMap: any = {};

  assetProperties: string[] = [
    "siteId",
    "assetServiceAddressId",
    "assetTonerAddressId",
    "assetBreakfixContactId",
    "assetTonerContactId",
    "assetInstallationContactId",
    "siteBreakfixContactId",
    "siteTonerContactId",
    "siteInstallationContactId",
    "siteTonerAddressId",
    "siteServiceAddressId",
    "itsmSiteId",
    "itsmSiteName",
    "defaultCustomShippingLabel1",
    "defaultCustomShippingLabel2",
  ];

  siteId: string;
  assetServiceAddressId: string;
  assetTonerAddressId: string;
  assetBreakfixContactId: string;
  assetTonerContactId: string;
  assetInstallationContactId: string;
  siteBreakfixContactId: string;
  siteTonerContactId: string;
  siteInstallationContactId: string;
  siteTonerAddressId: string;
  siteServiceAddressId: string;
  itsmSiteId: string;
  itsmSiteName: string;
  defaultCustomShippingLabel1: string;
  defaultCustomShippingLabel2: string;

  addressesInfo = {
    toner: {
      name: "consumableAddress",
      humanReadableTitle: "Consumable Address",
      id: "assetTonerAddressId",
      defaultId: "siteTonerAddressId",
      readOnly: false,
      defaultAddress: {
        deliveryType: "DOCK",
        customShippingLabel1: "",
        customShippingLabel2: "",
      },
      allowCreate: true,
      requireBpId: false,
    },
    service: {
      name: "serviceAddress",
      humanReadableTitle: "Service Address",
      id: "assetServiceAddressId",
      defaultId: "siteServiceAddressId",
      readOnly: false,
      defaultAddress: {
        deliveryType: null,
        customShippingLabel1: "",
        customShippingLabel2: "",
      },
      allowCreate: true,
      requireBpId: false,
    },
  };

  contactInfo = {
    toner: {
      name: "toner",
      humanReadableTitle: "Consumable Contact",
      id: "assetTonerContactId",
      defaultId: "siteTonerContactId",
      allowCreate: true,
      requireBpId: false,
    },
    breakfix: {
      name: "breakfix",
      humanReadableTitle: "Service Delivery Contact",
      id: "assetBreakfixContactId",
      defaultId: "siteBreakfixContactId",
      allowCreate: true,
      requireBpId: false,
    },
    installation: {
      name: "installation",
      humanReadableTitle: "Installation Contact",
      id: "assetInstallationContactId",
      defaultId: "siteInstallationContactId",
      allowCreate: true,
      requireBpId: false,
    },
  };

  constructor(
    public dialogService: DialogService,
    private appSettingsService: AppSettingsService,
    @Inject(forwardRef(() => AccessoryService)) private accessoryService: AccessoryService,
    @Inject(forwardRef(() => AssetService)) private assetService: AssetService,
    @Inject(forwardRef(() => ProjectService)) private projectService: ProjectService,
    @Inject(forwardRef(() => WorkflowService)) private workflowService: WorkflowService,
    @Inject(forwardRef(() => ContactService)) private contactService: ContactService,
    @Inject(forwardRef(() => AddressService)) private addressService: AddressService,
  ) {
    super(dialogService);
  }

  changedDartDevice() {
    if (!this.asset.isFutureFleet) return;

    const dartDevice = this.asset.availableDevices.find(it => it.id === this.asset.deviceId);
    if (!dartDevice) return;

    this.asset.dispositions = [new AssetDispositionModel(dartDevice.disposition)];
    this.asset.dispositionId = get(dartDevice, "disposition.id") as string || null;
    this.asset.dispositionText = get(dartDevice, "disposition.dispositionText") as string || "Unknown Disposition";

    this.asset.bundledAccessories = (dartDevice.bundledAccessories || [])
      .map(it => new AssetBundledAccessoryModel(it));

    for (const accessory of this.asset.bundledAccessories) {
      accessory.changed = true;
    }

    const availableBulkOrderItems = dartDevice.availableBulkOrderItems || [];
    this.asset.availableBulkOrderItems = availableBulkOrderItems;

    if (this.asset.bulkOrderAssetId) {
      if (!availableBulkOrderItems.some(it => it.id === this.asset.bulkOrderAssetId)) {
        this.form.controls["bulkOrderAssetId"].setValue("");
        this.form.controls["bulkOrderAssetId"].markAsDirty();
      }
    }

    const availableProducts = dartDevice.availableProducts || [];
    this.asset.availableProducts = availableProducts;

    if (!availableProducts.some(it => it.productId === this.asset.productId)) {
      this.form.controls["productId"].markAsDirty();
      if (availableProducts.length) {
        const product = availableProducts[availableProducts.length - 1];
        this.form.controls["productId"].setValue(product.productId);
        this.form.controls["productId"].markAsDirty();
        this.asset.productId = product.productId;
        this.asset.modelName = product.modelName;
        this.asset.productNumber = product.productNumber;
      } else {
        this.form.controls["productId"].setValue("");
        this.form.controls["productId"].markAsDirty();
        this.asset.modelName = "Not supported by ITSM";
        this.asset.productNumber = "Unknown";
        this.asset.productId = "";
      }
    }
  }

  updateBulkOrderData() {
    if (!this.asset.availableBulkOrderItems.length) {
      this.form.controls["useBulkOrdering"].setValue(false);
      this.form.controls["useBulkOrdering"].markAsDirty();
      return;
    }

    const form = this.form.getRawValue();

    if (form.useBulkOrdering) {
      const bulkOrder = this.asset.availableBulkOrderItems.find(it => it.id === form.bulkOrderId);
      if (bulkOrder) {
        this.form.controls["orderNumber"].setValue(bulkOrder.orderNumber);
        this.form.controls["orderDate"].setValue(bulkOrder.orderDate);
        this.form.controls["orderNumber"].markAsDirty();
        this.form.controls["orderDate"].markAsDirty();
      }
    }
  }

  updateAddressAndContactMaps() {
    // Safety code in case we get passed a null pointer
    this.addresses = this.addresses || [];
    this.contacts = this.contacts || [];

    // Set up the maps
    this.addressMap = this.addresses.reduce((acc, it) => { acc[it.id] = it; return acc; }, {});
    this.settableAddresses = this.addresses
      .filter(it => it.itsmBuildingId && it.itsmSiteId === this.itsmSiteId);
    this.settableAddressesMap = this.settableAddresses.reduce((acc, it) => { acc[it.id] = it; return acc; }, {});

    this.contactMap = this.contacts.reduce((acc, it) => { acc[it.id] = it; return acc; }, {});
    this.settableContacts = this.contacts.filter(it => it.itsmContactId);
    this.settableContactsMap = this.settableContacts.reduce((acc, it) => { acc[it.id] = it; return acc; }, {});
  }

  loadAddressAndContactInformation() {
    this.assetProperties.forEach((key) => { this[key] = this.asset[key] || null; });
    // Copy over defaults
    Object.values(this.addressesInfo)
      .forEach((it) => { this[it.id] = this[it.id] || this[it.defaultId] || null; });
    Object.values(this.contactInfo)
      .forEach((it) => { this[it.id] = this[it.id] || this[it.defaultId] || null; });

    // Load our default shipping labels into the default structures we pass to the next dialog
    Object.values(this.addressesInfo).forEach(({defaultAddress}) => {
      if (defaultAddress) {
        if (this.defaultCustomShippingLabel1) defaultAddress.customShippingLabel1 = this.defaultCustomShippingLabel1;
        if (this.defaultCustomShippingLabel2) defaultAddress.customShippingLabel2 = this.defaultCustomShippingLabel2;
      }
    });

    this.updateAddressAndContactMaps();
  }

  @toggleLoading()
  @handleErrors()
  async ready() {
    this.targetSystem = this.workflowService.workflowConfig.targetSystem;
    this.asset = await this.assetService.getAsset(this.assetId);
    this.loadAddressAndContactInformation();

    this.form = this.asset.formGroup;

    this.enableAdditionalFieldsForMigration = await this.appSettingsService.getFeaturePermission(
      "additionalFieldsForMigration",
      this.projectId,
    );

    const minDate = moment(this.asset.masterAgreementStartDate).toDate();
    this.installDateOpts = {minDate};
  }

  @handleErrors()
  async addStandaloneAccessory() {
    const {accessory, saved} = await this.accessoryService.createAccessory(this.asset.siteId, this.asset.id);

    if (!saved) return;

    accessory.original = false;
    accessory.changed = true;
    accessory.keep = true;

    this.asset.standaloneAccessories.push(accessory);
  }

  @handleErrors()
  async editStandaloneAccessory(originalAccessory: StandaloneAccessoryGqlModel) {
    originalAccessory.availableStandaloneAccessories = this.asset.availableStandaloneAccessories;

    const {accessory, saved} = await this.accessoryService.editAccessory(originalAccessory);

    if (!saved) return;

    accessory.export(originalAccessory);
    originalAccessory.changed = true;
    originalAccessory.keep = true;
  }

  removeStandaloneAccessory(accessory: StandaloneAccessoryGqlModel) {
    if (accessory.original) {
      accessory.keep = false;
      return;
    }

    const index = this.asset.standaloneAccessories.indexOf(accessory);
    if (index >= 0) {
      this.asset.standaloneAccessories.splice(index, 1);
    } else {
      accessory.keep = false;
    }
  }

  @handleErrors()
  async editContact(type: "toner" | "breakfix" | "installation"): Promise<void> {
    const contactInfo = this.contactInfo[type];
    if (!contactInfo) throw new Error("Contact type not found");

    const selectedTmcContactId = this[contactInfo.id];
    const modalResults = await this.assetService.selectAssetContact({
      projectId: this.projectId,
      allowCreate: contactInfo.allowCreate,
      requireBpId: contactInfo.requireBpId,
      selectedTmcContactId,
      itsmContacts: this.contacts,
      contactService: this.contactService,
      titleModifier: contactInfo.humanReadableTitle,
    });

    if (modalResults && modalResults.changed) {
      const foundContact = this.contacts.find(it => it.id === modalResults.selectedContactId);
      if (
        contactInfo.allowCreate &&
        !foundContact &&
        modalResults.createdContact &&
        modalResults.createdContact.id &&
        modalResults.createdContact.itsmContactId
      ) {
        // Update our list of addresses to include the newly created address
        this.contacts.push(modalResults.createdContact);
        this.updateAddressAndContactMaps();
        this[contactInfo.id] = modalResults.selectedContactId;
        await this.saveAddressesAndContacts();
      } else if (foundContact) {
        this[contactInfo.id] = modalResults.selectedContactId;
        await this.saveAddressesAndContacts();
      } else {
        this.error = new Error("Selected contact does not exist");
      }
    }
  }

  async saveAddressesAndContacts() {
    const params = {assetId: this.assetId};
    Object.values(this.addressesInfo).forEach((it) => {
      params[it.id] = (this[it.id] === this[it.defaultId] || !this.settableAddressesMap[this[it.id]]) ?
        null :
        this[it.id] || null;
      this[it.id] = params[it.id] || this[it.defaultId];
    });
    Object.values(this.contactInfo).forEach((it) => {
      params[it.id] = (this[it.id] === this[it.defaultId] || !this.settableContactsMap[this[it.id]]) ?
        null :
        this[it.id] || null;
      this[it.id] = params[it.id] || this[it.defaultId];
    });
    await this.assetService.setAssetContactAddressIdsItsm(params);
  }

  @handleErrors()
  async editAddress(type: "service" | "toner"): Promise<void> {
    const addressInfo = this.addressesInfo[type];
    if (!addressInfo) throw new Error("Address type not found");
    const modalResults = await this.assetService.selectAssetAddress({
      projectId: this.projectId,
      allowCreate: addressInfo.allowCreate,
      requireBpId: addressInfo.requireBpId,
      selectedTmcAddressId: this[addressInfo.id] || this[addressInfo.defaultId],
      itsmSiteId: this.itsmSiteId,
      itsmSiteName: this.itsmSiteName,
      itsmAddresses: this.settableAddresses,
      addressService: this.addressService,
      targetSystem: this.targetSystem,
      defaultAddress: addressInfo.defaultAddress,
      titleModifier: addressInfo.humanReadableTitle,
      hideDeliveryType: type === "service",
      buildingType: type === "service" ? "Service" : "Consumables",
    });

    if (modalResults && modalResults.changed) {
      const foundAddress = this.addresses.find(it => it.id === modalResults.selectedTmcAddressId);
      if (
        addressInfo.allowCreate &&
        !foundAddress &&
        modalResults.createdAddress &&
        modalResults.createdAddress.id &&
        modalResults.createdAddress.itsmBuildingId
      ) {
        // Update our list of addresses to include the newly created address
        this.addresses.push(modalResults.createdAddress);
        this.updateAddressAndContactMaps();
        this[addressInfo.id] = modalResults.selectedTmcAddressId;
        await this.saveAddressesAndContacts();
      } else if (foundAddress) {
        this[addressInfo.id] = modalResults.selectedTmcAddressId;
        await this.saveAddressesAndContacts();
      } else {
        this.error = new Error("Selected address does not exist");
      }
    }
  }

  @toggleLoading()
  @handleErrors()
  async save(): Promise<EditAssetDialogReturnModel> {
    this.loading = true;
    const {updated} = await this.assetService.saveAsset(this.asset);

    this.result.updated = updated;
    this.result.addresses = this.addresses;
    this.result.contacts = this.contacts;

    this.close();
    return this.result;
  }

  @handleErrors()
  async exit(): Promise<EditAssetDialogReturnModel> {
    this.result.updated = false;
    this.result.addresses = this.addresses;
    this.result.contacts = this.contacts;

    this.close();
    return this.result;
  }
}
